import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { RouterLink } from '@angular/router';
import { APP_PATHS, MIGRATION_FINDINGS_TABLE_COLUMNS_CONFIG, XEMEX_OID } from '@core/constants';
import { browserZone, zonedInstant } from '@core/helpers/slot-series.helper';
import { getUserFeaturePermissions } from '@core/models';
import {
  FindingClass,
  FindingResolution,
  FindingSeverity,
  FindingSubjectType,
  MigrationFinding,
} from '@core/models/migration-report.model';
import { CompanyService } from '@core/services';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { CardComponent } from '@custom-components/general/card/card.component';
import { TextButtonComponent } from '@custom-components/general/button/text-button/text-button.component';
import { TableComponent } from '@custom-components/general/table/table.component';
import {
  FilterGroup,
  TableFilterBarComponent,
  TypeaheadItem,
} from '@custom-components/general/table-filter-bar';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import {
  MIGRATION_REPORT_PAGE_SIZE,
  MigrationReportFilterChange,
  MigrationReportStore,
} from '@root/app/store/migration-report.store';
import { EMPTY, catchError, firstValueFrom } from 'rxjs';
import {
  MigrationFindingDialogComponent,
  MigrationFindingDialogData,
  migrationFindingDialogConfig,
} from '../../components/migration-finding-dialog/migration-finding-dialog.component';
import { findingLink } from '../../migration-finding-link';

const CLASSES: FindingClass[] = [
  'location_without_coordinates',
  'belgian_site_deferred',
  'timezone_unresolved',
  'multi_device_location',
  'partner_mismatch',
  'customer_change_history',
  'moved_device_truncated_history',
  'no_bindable_channel',
  'single_role_contested',
  'meter_role_applied',
  'secondary_emeter_role_assumed',
  'empty_label_channel',
  'register_reader_gap',
  'device_without_location',
  'device_without_provisioning_row',
  'dead_location_column',
  'seeding_failed',
  'group_location_without_installation',
  'group_device_membership',
  'service_without_installation',
  'service_ambiguous_installation',
];
const SUBJECT_TYPES: FindingSubjectType[] = [
  'location',
  'installation',
  'device',
  'binding',
  'group',
  'service',
];
const SEVERITIES: FindingSeverity[] = ['blocking', 'action', 'informational'];
const RESOLUTIONS: FindingResolution[] = ['open', 'acknowledged', 'no_action', 'resolved_by_data'];

const SEVERITY_STATE: Record<FindingSeverity, string> = {
  blocking: 'error',
  action: 'warning',
  informational: 'neutral',
};
const RESOLUTION_STATE: Record<FindingResolution, string> = {
  open: 'warning',
  acknowledged: 'info',
  no_action: 'neutral',
  resolved_by_data: 'success',
};

const KEY = 'installations.migrationReport.';

@Component({
  selector: 'xemex-migration-report-page',
  imports: [
    CardComponent,
    PageBodyComponent,
    PageHeaderComponent,
    RouterLink,
    TableComponent,
    TableFilterBarComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  providers: [MigrationReportStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './migration-report-page.component.html',
  styleUrl: './migration-report-page.component.scss',
})
export class MigrationReportPageComponent {
  protected readonly store = inject(MigrationReportStore);
  private readonly translate = inject(TranslateService);
  private readonly companyService = inject(CompanyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);

  protected readonly columns = MIGRATION_FINDINGS_TABLE_COLUMNS_CONFIG;
  protected readonly pageSize = MIGRATION_REPORT_PAGE_SIZE;
  protected readonly severities = SEVERITIES;
  protected readonly resolutions = RESOLUTIONS;

  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    {
      label: this.translate.instant('sidebarMenu.installations'),
      routerLink: '/' + APP_PATHS.Installations,
    },
    { label: this.translate.instant(KEY + 'title') },
  ];

  protected readonly selection = signal<MigrationReportFilterChange>({ resolution: 'open' });
  // The filter bar reads its starting filters once, so a class chosen in the summary mounts a new
  // bar that starts from the selection.
  protected readonly barVersion = signal(0);
  private readonly partnerNames = signal<Record<string, string>>({});
  private readonly requestedPartners = new Set<string>();

  protected readonly canResolve = computed(() =>
    getUserFeaturePermissions(
      this.userStore.user(),
      this.authStore.permissions(),
      XEMEX_OID,
    ).includes('resolve_migration_finding'),
  );

  // The summary counts every resolution, so these are totals and not the open backlog.
  protected readonly classCounts = computed(() => {
    const counts = this.store.summary()?.byClass ?? {};
    return CLASSES.map(findingClass => ({ findingClass, count: counts[findingClass] ?? 0 }))
      .filter(entry => entry.count > 0)
      .sort((a, b) => b.count - a.count);
  });

  protected readonly tableData = computed(
    () =>
      this.store.page().findings.map(finding => this.toRow(finding)) as unknown as Record<
        string,
        unknown
      >[],
  );
  protected readonly totalCount = computed(() => this.store.page().numberOfItems);

  private readonly partnerSearch = (query: string): Promise<TypeaheadItem[]> =>
    firstValueFrom(
      this.companyService.getListCompaniesFilter({ limit: 10, offset: 0, partnerName: query }),
    ).then(res => (res.partners ?? []).map(p => ({ id: p.partnerOid, label: p.partnerName })));

  private readonly partnerLabel = (oid: string): Promise<TypeaheadItem | null> =>
    firstValueFrom(this.companyService.getCompanyDetail(oid))
      .then(partner => ({ id: oid, label: partner.partnerName }))
      .catch(() => null);

  protected readonly filterGroups = computed<FilterGroup[]>(() => [
    {
      key: 'findingClass',
      label: this.translate.instant(KEY + 'filter.class'),
      selection: 'single',
      options: CLASSES.map(value => this.classLabel(value)),
    },
    {
      key: 'subjectType',
      label: this.translate.instant(KEY + 'filter.subjectType'),
      selection: 'single',
      options: SUBJECT_TYPES.map(value => this.subjectTypeLabel(value)),
    },
    {
      key: 'resolution',
      label: this.translate.instant(KEY + 'filter.resolution'),
      selection: 'single',
      options: RESOLUTIONS.map(value => this.resolutionLabel(value)),
    },
    {
      key: 'partnerOid',
      label: this.translate.instant(KEY + 'filter.partner'),
      type: 'typeahead',
      selection: 'single',
      search: this.partnerSearch,
      resolve: this.partnerLabel,
      placeholder: this.translate.instant('general.placeholderInput'),
    },
  ]);

  protected readonly initialFilters = computed<Record<string, string[]>>(() => {
    const { findingClass, subjectType, resolution, partnerOid } = this.selection();
    const filters: Record<string, string[]> = {};
    if (findingClass) filters['findingClass'] = [this.classLabel(findingClass)];
    if (subjectType) filters['subjectType'] = [this.subjectTypeLabel(subjectType)];
    if (resolution) filters['resolution'] = [this.resolutionLabel(resolution)];
    if (partnerOid) filters['partnerOid'] = [partnerOid];
    return filters;
  });

  constructor() {
    this.store.loadSummary();
    this.applySelection();

    effect(() => {
      const oids = this.store.page().findings.flatMap(finding => finding.partnerOid ?? []);
      untracked(() => this.requestPartnerNames(oids));
    });
  }

  protected severityLabel(severity: FindingSeverity): string {
    return this.translate.instant(`${KEY}severities.${severity}`);
  }

  protected resolutionLabel(resolution: FindingResolution): string {
    return this.translate.instant(`${KEY}resolutions.${resolution}`);
  }

  protected classLabel(findingClass: FindingClass): string {
    return this.translate.instant(`${KEY}classes.${findingClass}`);
  }

  protected subjectTypeLabel(subjectType: FindingSubjectType): string {
    return this.translate.instant(`${KEY}subjectTypes.${subjectType}`);
  }

  protected onClassChosen(findingClass: FindingClass): void {
    const chosen = this.selection().findingClass === findingClass ? undefined : findingClass;
    this.selection.update(selection => ({ ...selection, findingClass: chosen }));
    this.barVersion.update(version => version + 1);
    this.applySelection();
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    this.selection.set({
      findingClass: CLASSES.find(value => this.classLabel(value) === filters['findingClass']?.[0]),
      subjectType: SUBJECT_TYPES.find(
        value => this.subjectTypeLabel(value) === filters['subjectType']?.[0],
      ),
      resolution: RESOLUTIONS.find(
        value => this.resolutionLabel(value) === filters['resolution']?.[0],
      ),
      partnerOid: filters['partnerOid']?.[0] || undefined,
    });
    this.applySelection();
  }

  protected onPageChange(event: PageEvent): void {
    this.store.loadPage(event.pageIndex);
  }

  protected openFinding(row: Record<string, unknown>): void {
    const finding = this.store.page().findings.find(item => item.findingOid === row['findingOid']);
    if (!finding) return;
    const data: MigrationFindingDialogData = {
      finding,
      canResolve: this.canResolve(),
      partnerName: finding.partnerOid ? (this.partnerNames()[finding.partnerOid] ?? null) : null,
    };
    const ref = this.dialog.open<
      MigrationFindingDialogComponent,
      MigrationFindingDialogData,
      MigrationFinding
    >(MigrationFindingDialogComponent, migrationFindingDialogConfig(data));
    const dialog = ref.componentInstance;
    // An acknowledge moves the finding out of the open list and changes the counts, and a dialog
    // that found the finding gone has the same effect.
    ref.afterClosed().subscribe(result => {
      if (result === undefined && !dialog.stale()) return;
      this.store.loadSummary();
      this.store.reload();
    });
  }

  protected reloadSummary(): void {
    this.store.loadSummary();
  }

  protected reloadPage(): void {
    this.store.reload();
  }

  private applySelection(): void {
    this.store.applyFilter(this.selection());
  }

  private requestPartnerNames(oids: string[]): void {
    for (const oid of new Set(oids)) {
      if (this.requestedPartners.has(oid)) continue;
      this.requestedPartners.add(oid);
      this.companyService
        .getCompanyDetail(oid)
        .pipe(
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(partner =>
          this.partnerNames.update(names => ({ ...names, [oid]: partner.partnerName })),
        );
    }
  }

  private toRow(finding: MigrationFinding): Record<string, unknown> {
    return {
      findingOid: finding.findingOid,
      className: this.classLabel(finding.findingClass),
      subject: finding.subjectLabel || finding.subjectId,
      severityLabel: this.severityLabel(finding.severity),
      severityState: SEVERITY_STATE[finding.severity],
      resolutionLabel: this.resolutionLabel(finding.resolution),
      resolutionState: RESOLUTION_STATE[finding.resolution],
      partner: finding.partnerOid ? (this.partnerNames()[finding.partnerOid] ?? '') : '',
      lastSeen: zonedInstant(finding.lastSeenAt, browserZone(), 'D MMM YYYY HH:mm'),
      link: findingLink(finding),
    };
  }
}
